/**
 * NOTES ABOUT THESE MODELS:
 *    - all event/property/substring names need to be _generic_, such that, if the name or implementation ever changes,
 *      the record key still reflects the functionality and can be re-used without changing.
 *      e.g.: we should not use 'radio-tile' as the key, but instead 'select-option', or something similar
 */

// this model is used to define all possible mixpanel events and their names
export const mixpanelEventNameModel = {
    register: 'Register',
    'create-account': 'Create account',
    login: 'Login',
    'password-forgotten': 'Password forgotten',
    'telephone-link': 'Telephone link',
    'header-search': 'Header search',
    filter: 'Filter',
    'sort-dropdown': 'Sort dropdown',
    'product-card': 'Product card',
    'configure-this-door': 'Configure this door',
    'continue-configuration': 'Continue configuration',
    'radio-tile1': 'Radio tile',
    'radio-tile2': 'Radio tile',
    'radio-tile': 'Radio tile',
    explanation: 'Explanation',
    'request-offer': 'Finish request',
    'share-project-installer': 'Share project with Installer',
    'share-project-consumer': 'Share project with Consumer',
    'form-close': 'Close Form',
    'form-complete': 'Complete Form',
    'copy-productId': 'Copy Verkoopcode',
    'switch-user': 'Switch user role',
    'show-spec': 'Show specification',
} as const;

// this model is used to append a suffix to an event name, in case there are multiple, or additional clarification needs to be provided to the event name
export const mixpanelEventNameSubstringModel = {
    interact: 'Interact',
    successful: 'Successful',
    unsuccesful: 'Unsuccessful',
    select: 'Select',
    'select-option': 'Select option',
    submit: 'Submit',
    open: 'Open',
    close: 'Close',
} as const;

// this model is used to define all possible properties in mixpanel that we support
export const mixpanelPropertyNameModel = {
    path: 'Path',
    formcompleted: 'FormCompleted',
    'error-message': 'Error message',
    'telephone-number': 'Telephone number',
    'search-term': 'Search term',
    'filter-variant': 'Filter variant',
    'filter-serie': 'Filter serie',
    'filter-prijs': 'Filter prijs',
    basemodelid: 'baseModelID',
    'total-price-configuration-incl': 'Total price configuration Incl',
    'total-price-configuration-excl': 'Total price configuration Excl',
    'stock-status-base-model': 'Stock status Base model',
    'delivery-time-base-model': 'Delivery time Base model',
    'configuration-quantity': 'Configuration quantity',
    'stock-status-variant': 'Stock status variant',
    'delivery-time-variant': 'Delivery time variant',
    'door-height': 'Door height',
    'door-width': 'Door width',
    'door-variant': 'Door variant',
    'door-rotation-direction': 'Door rotation direction',
    'glass-choice': 'Glass choice',
    'glass-type': 'Glass type',
    'selection-value': 'Selection value',
    'selection-label': 'Selection label',
    user: 'User',
    variant: 'Variant',
    section: 'Section',
    stock: 'Stock',
    'delivery-time': 'Delivery time',
    'order-stock': 'Order.stock',
    'order-deliverytime': 'Order.deliverytime',
    key: 'Key',
    'accessory-scharnieren': 'Accessory / Scharnieren',
    'finishing-armschaven': 'Finishing / Armschaven',
    label: 'Label',
    value: 'Value',
    form: 'Form',
    productId: 'Verkoopcode',
} as const;

export const mixpanelEventNameMapping: Partial<Record<keyof MixpanelEventNameModel, string>> = {
    register: 'Register',
};

export type MixpanelProperty = keyof typeof mixpanelPropertyNameModel;

type MixpanelEventNameModel = typeof mixpanelEventNameModel;
type MixpanelEventNameSubstringModel = typeof mixpanelEventNameSubstringModel;
export type MixpanelEventName =
    | keyof MixpanelEventNameModel
    | `${keyof MixpanelEventNameModel} - ${keyof MixpanelEventNameSubstringModel}`;

export type MixpanelGenericProps = 'page' | 'name';

export const mixpanelEventStructures = {
    register: ['path'],
    'create-account - unsuccesful': ['userid'],
    'create-account - successful': ['error-message'],
    'create-account': ['formcompleted', 'error-message'],
    'login - successful': ['path', 'userid'],
    'login - unsuccesful': ['path', 'error-message'],
    'password-forgotten - select': ['path', 'userid'],
    'password-forgotten - submit': ['path', 'userid'],
    'telephone-link': ['telephone-number'],
    'header-search': ['search-term', 'userid'],
    'filter - open': ['step', 'userid'],
    'filter - submit': ['step', 'filter-variant', 'filter-serie', 'filter-prijs', 'userid'],
    'sort-dropdown - interact': ['step', 'label', 'userid'],
    'sort-dropdown - select-option': ['step', 'label', 'value', 'userid'],
    'product-card': ['step', 'basemodelid', 'userid'],
    'configure-this-door': [
        'basemodelid',
        'total-price-configuration-incl',
        'total-price-configuration-excl',
        'stock-status-base-model',
        'delivery-time-base-model',
    ],
    'continue-configuration': [
        'basemodelid',
        'total-price-configuration-incl',
        'total-price-configuration-excl',
        'stock-status-base-model',
        'delivery-time-base-model',
    ],
    'radio-tile1': [
        'step',
        'basemodelid',
        'configuration-quantity',
        'total-price-configuration-incl',
        'total-price-configuration-excl',
        'stock-status-base-model',
        'delivery-time-base-model',
        'stock-status-variant',
        'delivery-time-variant',
        'door-height',
        'door-width',
        'door-variant',
        'door-rotation-direction',
        'glass-choice',
        'glass-type',
    ],
    'radio-tile': ['step', 'role'],
    'explanation - open': ['step', 'basemodelid', 'selection-value', 'selection-label'],
    'explanation - close': [
        'step',
        'user',
        'variant',
        'basemodelid',
        'selection-value',
        'selection-label',
        'selection-label',
    ],
    'radio-tile2': [
        'section',
        'basemodelid',
        'configuration-quantity',
        'total-price-configuration-incl',
        'total-price-configuration-excl',
        'stock',
        'delivery-time',
        'order-stock',
        'order-deliverytime',
        'door-height',
        'door-width',
        'door-variant',
        'door-rotation-direction',
        'key',
        'glass-type',
        'accessory-scharnieren',
        'accessory-scharnieren',
    ],
    'direct-order': [],
    'request-offer': [],
    'cancel-offer': [],
    'form-close': ['form'],
    'form-complete': ['form'],
    'confirmation-close': [],
    'confirmation-submit': [],
    'copy-productId': ['productId'],
    'share-project': [],
    'migrate-project': [],
    'switch-user': ['role'],
    'show-spec': ['basemodelid', 'value'],
    'modal-close': [],
} as const;

export type MixpanelEventStructures = typeof mixpanelEventStructures;
